import { createFileRoute } from '@tanstack/react-router';
import type { ReactNode } from 'react';

export const Route = createFileRoute('/_site/legal')({
  head: () => ({
    meta: [
      { title: 'Legal, licences and notices | Everwake' },
      {
        name: 'description',
        content:
          "Everwake's SRD 5.2.1 CC-BY-4.0 attribution, AI game master disclosure, adults-only notice and privacy contact.",
      },
      { property: 'og:title', content: 'Everwake — legal, licences and notices' },
      {
        property: 'og:description',
        content: 'SRD 5.2.1 attribution, AI disclosure, 18+ notice and privacy contact.',
      },
      { property: 'og:type', content: 'article' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
  }),
  component: Legal,
});

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="border-t border-border/70 py-8">
      <h2 className="font-display text-2xl">{title}</h2>
      <div className="mt-3 space-y-3 text-muted-foreground">{children}</div>
    </section>
  );
}

function Legal() {
  return (
    <div className="mx-auto max-w-3xl px-5 py-16">
      <p className="rule-line">Legal</p>
      <h1 className="mt-4 font-display text-4xl md:text-5xl">Licences and notices</h1>

      <Section title="Licence and attribution">
        <p>
          Everwake is 5E-compatible and built on the System Reference Document 5.2.1, used under the Creative Commons Attribution 4.0 International licence (CC-BY-4.0).
        </p>
        <p>
          Everwake is an independent game with its own world, rules engine and content. It has no affiliation with or endorsement from the publisher of the trademarked game.
        </p>
      </Section>

      <Section title="AI notice">
        <p>
          Your game master is an AI system. The provider and model in use are shown inside every session. A deterministic engine owns the rules, the world and the dice; the AI narrates the validated outcome supplied by the engine.
        </p>
      </Section>

      <Section title="Adults only">
        <p>
          Everwake is for players aged eighteen or over. Its fiction, violence and consequences are written for an adult audience. Before every session, your chosen lines shape the content available to the story.
        </p>
      </Section>

      <Section title="Privacy">
        <p>
          Everwake is operated by a company registered in the Netherlands, which is the controller of personal data collected through this site and the game. A full privacy statement will be published here before launch.
        </p>
        <p>
          Until then, the details shown on the playtest form are used to contact you about an Everwake playtest.
        </p>
      </Section>
    </div>
  );
}
